/**
 * Shape Lab: turn a dial from 3 to 12 corners, name the shape, then drop it
 * into a pile. Round shapes roll, flat-sided shapes stack — the property is
 * something you watch, not something you are told.
 */

import { useRef, useState } from 'preact/hooks';
import type { ComponentChildren, JSX } from 'preact';
import { ArrowDownToLine, Home, Trash2 } from 'lucide-preact';
import type { Stroke } from '../../engine/stroke.ts';
import { createBody } from '../../engine/body.ts';
import { regularPolygon } from '../../engine/geom.ts';
import { World } from '../../engine/world.ts';
import { drawBody, drawBodyShadow, drawSparks, drawStroke, spawnSparks, updateSparks } from '../../engine/render.ts';
import type { Spark } from '../../engine/render.ts';
import { partyColors, token } from '../../engine/palette.ts';
import { degreeForSize, play, playImpact } from '../../engine/audio.ts';
import { useSurface } from '../../hooks/useSurface.ts';
import { Coach, ConfirmSheet, CrayonRow, MagicButton, Stepper, ToyButton } from '../../ui/controls.tsx';

const SHAPE_NAMES: Record<number, string> = {
  3: 'Triangle',
  4: 'Square',
  5: 'Pentagon',
  6: 'Hexagon',
  7: 'Heptagon',
  8: 'Octagon',
  9: 'Nonagon',
  10: 'Decagon',
  11: 'Hendecagon',
  12: 'Dodecagon',
};

interface Props {
  onExit: () => void;
  tabs: ComponentChildren;
}

function polygonStroke(sides: number, radius: number, color: string): Stroke {
  const pts = regularPolygon(sides, radius);
  const closed = [...pts, pts[0]];
  return {
    brush: 'marker',
    color: 'rgba(44, 37, 69, 0.85)',
    width: 5,
    points: closed,
    speeds: closed.map(() => 0),
    seed: sides,
    fill: color,
  };
}

export function ShapeToy({ onExit, tabs }: Props): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const worldRef = useRef(new World({ width: 1, height: 1, gravity: 1900 }));
  const sparksRef = useRef<Spark[]>([]);
  const spinRef = useRef(0);

  const [sides, setSides] = useState(5);
  const [color, setColor] = useState(() => token('--crayon-teal'));
  const [dropped, setDropped] = useState(0);
  const [askClear, setAskClear] = useState(false);

  const live = useRef({ sides, color });
  live.current = { sides, color };

  useSurface(canvasRef, {
    onResize: ({ width, height }) => worldRef.current.resize(width, height),
    onFrame: (ctx, dt, { width, height }) => {
      const world = worldRef.current;
      world.update(dt);
      updateSparks(sparksRef.current, dt);
      spinRef.current += dt * 0.5;

      for (const impact of world.impacts) playImpact('pop', Math.min(1, impact.speed / 1600), degreeForSize(impact.radius));

      ctx.fillStyle = token('--paper');
      ctx.fillRect(0, 0, width, height);

      // The dial's current shape, previewed big and slowly turning.
      const previewR = Math.min(width, height) * 0.17;
      const cx = width / 2;
      const cy = height * 0.3;
      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(spinRef.current);
      drawStroke(ctx, polygonStroke(live.current.sides, previewR, live.current.color));
      ctx.restore();

      // Corner dots — the thing being counted.
      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(spinRef.current);
      ctx.fillStyle = token('--ink');
      for (const p of regularPolygon(live.current.sides, previewR)) {
        ctx.beginPath();
        ctx.arc(p.x, p.y, 5.5, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore();

      for (const body of world.bodies) drawBodyShadow(ctx, body, height);
      for (const body of world.bodies) drawBody(ctx, body);
      drawSparks(ctx, sparksRef.current);
    },
    onDown: (p) => {
      const body = worldRef.current.pick(p.pos);
      if (body) {
        body.vel = { x: (Math.random() - 0.5) * 300, y: -560 };
        body.angVel = (Math.random() - 0.5) * 8;
        body.asleep = false;
        spawnSparks(sparksRef.current, body.pos, partyColors(), 8, 220);
        play('pop', 0.6);
      }
    },
  });

  const drop = () => {
    const world = worldRef.current;
    const radius = Math.max(26, Math.min(48, world.width * 0.075));
    const at = { x: world.width * (0.3 + Math.random() * 0.4), y: world.height * 0.3 };
    const outline = regularPolygon(live.current.sides, radius).map((p) => ({ x: p.x + at.x, y: p.y + at.y }));
    const body = createBody(outline, [polygonStroke(live.current.sides, radius, live.current.color)], {
      behavior: 'normal',
      angVel: (Math.random() - 0.5) * 4,
    });
    world.add(body);
    while (world.bodies.length > 40) world.remove(world.bodies[0].id);
    setDropped(world.bodies.length);
    play('whoosh', 0.5);
  };

  const clear = () => {
    for (const b of worldRef.current.bodies) spawnSparks(sparksRef.current, b.pos, partyColors(), 5, 160);
    worldRef.current.clear();
    setDropped(0);
    setAskClear(false);
    play('whoosh', 0.7);
  };

  return (
    <div class="screen">
      <div class="stage">
        <canvas ref={canvasRef} aria-label="Shape lab" />
        <div class="topbar">
          <ToyButton round label="Home" hideLabel onClick={onExit}>
            <Home size={24} strokeWidth={2.5} />
          </ToyButton>
          <ToyButton round label="Clear the pile" hideLabel disabled={dropped === 0} onClick={() => setAskClear(true)}>
            <Trash2 size={24} strokeWidth={2.5} />
          </ToyButton>
        </div>
        <p class="readout">
          <strong>{SHAPE_NAMES[sides]}</strong>
          <span>
            {sides} sides · {sides} corners
          </span>
        </p>
        {dropped === 0 && <Coach>Pick a shape. Drop it.</Coach>}
      </div>

      <div class="dock">
        {tabs}
        <CrayonRow value={color} onChange={setColor} />
        <div class="dock__row">
          <Stepper value={sides} min={3} max={12} label="sides" onChange={setSides} />
          <MagicButton label="Drop it" accent={color} onClick={drop}>
            <ArrowDownToLine size={26} strokeWidth={2.75} />
          </MagicButton>
        </div>
      </div>

      {askClear && <ConfirmSheet title="Clear the pile?" confirmLabel="Clear it" onConfirm={clear} onCancel={() => setAskClear(false)} />}
    </div>
  );
}
